import * as React from 'react'
import { Theme } from '@mui/material/styles'
import { OnSearchAddressClick, Props as ResultItemProps } from '../typings'
import Stack from '@mui/material/Stack'
import { NameValue } from '../NameValue/NameValue'
import { useData } from './hooks'
import Box from '@mui/material/Box'
import { TotalValue } from '../TotalValue/TotalValue'
import { LocationLink } from '@user/features/GlobalSearch/LocationLink/LocationLink'
import { formatUSPhoneNumber } from '@user/features/GlobalSearch/ResultItem/utils'
import AddressMapLink from '@user/features/MapView/AddressMapLink/AddressMapLink'
import { useCallback } from 'react'
import Typography from '@mui/material/Typography'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'
import { mapEntityTypeToEnum } from '../utils/functions'

export type Props = Pick<
  ResultItemProps,
  'searchItem' | 'searchText' | 'onLocationClick' | 'recent'
> & {
  highlightColor: string
  onSearchAddressClick?: OnSearchAddressClick
}

export function ExtraInfo(props: Props) {
  const {
    searchText,
    onLocationClick,
    highlightColor,
    onSearchAddressClick,
    recent,
    searchItem: {
      age,
      altPhone,
      address,
      phone,
      createdBy,
      email,
      entityType,
      owner,
      ownerIsActive,
      salesFunnel,
      salesTeam,
      services,
      totalValue,
      locationCode,
      externalLinkId,
      externalLinkType,
      entityId,
      latitude,
      longitude,
      serviceLocationId,
    },
  } = props
  const { createdTimeFormatted, isTabletOrMobile } = useData(props)

  const ownerValue = React.useMemo(() => {
    if (
      ['Lead', 'Opportunity'].includes(entityType) &&
      ownerIsActive === false
    ) {
      return `${owner} (Inactive)`
    }
    return owner
  }, [owner, ownerIsActive, entityType])

  const mapViewEnabled = useMapViewFeatureEnabled()

  const itemEntityType = React.useMemo(
    () => mapEntityTypeToEnum(entityType),
    [entityType],
  )

  const handleAddressClick = useCallback(() => {
    if (!onSearchAddressClick || !latitude || !longitude) return

    onSearchAddressClick({
      location: new H.geo.Point(latitude ?? 0, longitude ?? 0),
      entityId,
      entityType: itemEntityType,
      serviceLocationId,
    })
  }, [
    onSearchAddressClick,
    latitude,
    longitude,
    entityId,
    itemEntityType,
    serviceLocationId,
  ])

  return (
    <Stack direction="row" width="100%">
      <Box
        sx={{ flexGrow: 1, marginRight: (theme: Theme) => theme.spacing(1) }}
      >
        <Stack direction={'column'}>
          {recent && (
            <>
              {entityType !== 'DraftLead' && locationCode && (
                <LocationLink
                  highlightColor={highlightColor}
                  locationCode={locationCode}
                  externalLinkId={externalLinkId}
                  externalLinkType={externalLinkType}
                  onClick={onLocationClick}
                  searchText={searchText}
                />
              )}

              {entityType !== 'DraftLead' && isTabletOrMobile && (
                <NameValue
                  {...props}
                  name={'Total Value'}
                  value={totalValue}
                  variant="currency"
                />
              )}

              {mapViewEnabled ? (
                <Stack direction="row" alignItems="flex-start">
                  {address && (
                    <Typography variant="subtitle2" component="span">
                      Address:&nbsp;&nbsp;
                    </Typography>
                  )}

                  <Box sx={{ mt: '-2px' }}>
                    <AddressMapLink
                      address={address ?? ''}
                      entityId={entityId}
                      entityType={itemEntityType}
                      latitude={latitude}
                      longitude={longitude}
                      onClick={handleAddressClick}
                    >
                      {address}
                    </AddressMapLink>
                  </Box>
                </Stack>
              ) : (
                <NameValue
                  {...props}
                  name="Address"
                  value={address}
                  variant="string"
                />
              )}
            </>
          )}

          {!recent && entityType !== 'DraftLead' && isTabletOrMobile && (
            <NameValue
              {...props}
              name={'Total Value'}
              value={totalValue}
              variant="currency"
            />
          )}

          <NameValue
            {...props}
            name={'Email'}
            value={email}
            variant="string"
            highlight={true}
          />

          {recent ? (
            <NameValue
              {...props}
              name={'Phone'}
              value={formatUSPhoneNumber(phone)}
              variant="string"
            />
          ) : (
            <NameValue
              {...props}
              name={'Alt. Phone'}
              value={altPhone}
              variant="phone"
              highlight={true}
            />
          )}

          {entityType !== 'DraftLead' && (
            <NameValue
              {...props}
              name={'Age'}
              value={`${age.toString()} day${age === 1 ? '' : 's'}`}
              variant="string"
            />
          )}
          <NameValue
            {...props}
            name={'Date Created'}
            value={createdTimeFormatted}
            variant="string"
          />
          <NameValue
            {...props}
            name={'Created By'}
            value={createdBy ?? '-'}
            variant="string"
          />
          <NameValue
            {...props}
            name={'Services'}
            value={services}
            variant="array"
          />

          <NameValue
            {...props}
            name={'Owner'}
            value={ownerValue}
            variant="string"
          />
          <NameValue
            {...props}
            name={'Sales Funnel'}
            value={salesFunnel}
            variant="string"
          />
          <NameValue
            {...props}
            name={'Sales Team'}
            value={salesTeam}
            variant="string"
          />
        </Stack>
      </Box>
      {entityType !== 'DraftLead' && !isTabletOrMobile && (
        <Box sx={{ display: 'flex', flexGrow: 0, alignItems: 'center' }}>
          <TotalValue totalValue={totalValue} />
        </Box>
      )}
    </Stack>
  )
}
